import React from 'react';
import { AvTooltip, SEVERITY_LABELS, severityBackgroundColor } from '@fabric-ui/components';
import { Transition as SeverityTransition } from '@fabric-ui/icons';
import { dotStyle, flex, getCssVarColorWithOpacity } from '@fabric-ui/theme';
import { abbreviateNumber, iconSize } from '@fabric-ui/utils';
import { Box, Divider } from '@mui/material';
import Pluralize from 'pluralize';
import { AbstractSeverityBarItem, ISeverityBarItemValue, ISeverityCompareWidget, ITooltipContent } from './types';

const SeverityCompareWidget: React.FC<ISeverityCompareWidget> = ({ data = [], entityTypeId = 'Finding' }) => (
  <Box sx={{ height: '100%', ...flex.justifyBetweenEnd }}>
    {data.map(item => {
      const { severity, now, prev } = item;
      return (
        <Box sx={{ ...flex.col, gap: 1, height: '100%' }}>
          <AvTooltip
            title={<TooltipContent prev={prev} now={now} severity={severity} entityTypeId={entityTypeId} />}
            muiProps={{
              placement: 'top',
              componentsProps: {
                popper: { sx: { '& .MuiTooltip-tooltip': { backgroundColor: 'var(--color-surface-immutable-darkGrey)' } } },
              },
            }}>
            <Box
              sx={{
                ...flex.justifyCenter,
                ':hover': { backgroundColor: getCssVarColorWithOpacity('var(--color-surface-interactive-secondary-default)', 0.5) },
                height: '100%',
              }}>
              <Box sx={{ ...flex.itemsEnd, gap: '3px' }}>
                <SeverityBarItem item={prev} severity={severity} />
                <SeverityTransition style={{ ...iconSize(16), color: 'var(--color-content-base-tertiary)' }} />
                <SeverityBarItem item={now} severity={severity} isCurrent />
              </Box>
            </Box>
          </AvTooltip>
          <Box sx={{ ...flex.center, fontSize: 12, color: 'var(--color-content-base-primary)' }}>{SEVERITY_LABELS[severity]}</Box>
        </Box>
      );
    })}
  </Box>
);
const SeverityBarItem: React.FC<AbstractSeverityBarItem & { item: ISeverityBarItemValue }> = ({ severity, item, isCurrent }) => {
  const valueStyle = {
    fontSize: 12,
    fontWeight: isCurrent ? 600 : 500,
    color: isCurrent ? 'var(--color-content-base-primary)' : 'var(--color-content-base-tertiary)',
  };
  return (
    <Box sx={{ ...flex.colCenter }}>
      <AvTooltip title={item.value.toLocaleString()}>
        <Box sx={valueStyle}>{abbreviateNumber(item.value)}</Box>
      </AvTooltip>
      <Box
        sx={{
          width: '20px',
          height: item.height,
          backgroundColor: severityBackgroundColor(severity),
        }}
      />
    </Box>
  );
};
const TooltipContent: React.FC<ITooltipContent> = ({ severity, prev, now, entityTypeId }) => (
  <Box sx={{ ...flex.col, p: 1, gap: 1 }}>
    <div>
      {SEVERITY_LABELS[severity]} {Pluralize(entityTypeId)}
    </div>
    <Divider sx={{ borderColor: 'var(--color-content-immutable-grey)' }} />
    <div>
      <Box sx={{ ...flex.justifyBetweenEnd }}>
        <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
          <Box
            sx={{
              ...dotStyle(severityBackgroundColor(severity)),
              border: '1px solid var(--color-border-base-secondary)',
            }}
          />
          <div>Original Severity Score</div>
        </Box>
        <Box sx={{ ml: 1 }}>{abbreviateNumber(prev.value)}</Box>
      </Box>
      <Box sx={{ ...flex.justifyBetweenEnd }}>
        <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
          <Box
            sx={{
              ...dotStyle(severityBackgroundColor(severity)),
              border: '1px solid var(--color-border-base-secondary)',
            }}
          />
          <div>Severity Score</div>
        </Box>
        <Box sx={{ ml: 1 }}>{abbreviateNumber(now.value)}</Box>
      </Box>
    </div>
  </Box>
);

export default SeverityCompareWidget;
